import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Users,
  FileText,
  BarChart3,
  Shield,
  LayoutTemplate,
  Calculator,
  DollarSign,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import OverviewTab from "./admin/OverviewTab";
import UsersTab, { type UserStats } from "./admin/UsersTab";
import FormsTab, { type FormStats } from "./admin/FormsTab";

interface GlobalStats {
  total_users: number;
  total_forms: number;
  total_sessions: number;
  total_completed: number;
  total_pdf_downloads: number;
}

export default function Admin() {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const { role, isLoading: planLoading } = useUserPlan();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserStats[]>([]);
  const [forms, setForms] = useState<FormStats[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    total_users: 0,
    total_forms: 0,
    total_sessions: 0,
    total_completed: 0,
    total_pdf_downloads: 0,
  });
  const [activeTab, setActiveTab] = useState<"overview" | "users" | "forms">("overview");
  const [deleteUser, setDeleteUser] = useState<{ id: string; email: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sendingGiftTo, setSendingGiftTo] = useState<string | null>(null);

  const handleSendGift = async (u: { id: string; email: string }) => {
    setSendingGiftTo(u.id);
    try {
      const { data, error } = await supabase.functions.invoke("send-gift", {
        body: { user_id: u.id, email: u.email },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast({
        title: "Presente enviado 🎁",
        description: `${u.email} recebeu o e-mail com 1 mês de Pro grátis.`,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao enviar presente",
        description: error?.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setSendingGiftTo(null);
    }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authLoading && !planLoading && isAuthenticated && role !== "admin") {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para acessar esta página.",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [authLoading, planLoading, isAuthenticated, role, navigate, toast]);

  useEffect(() => {
    if (isAuthenticated && role === "admin") {
      fetchData();
    }
  }, [isAuthenticated, role]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, email, created_at");
      if (profilesError) throw profilesError;
      if (!profiles) throw new Error("Failed to fetch profiles");

      const { data: allForms, error: formsError } = await supabase
        .from("forms")
        .select("id, title, slug, is_active, user_id");
      if (formsError) throw formsError;

      const { data: allSessions, error: sessionsError } = await supabase
        .from("form_sessions")
        .select("id, form_id, status, pdf_url");
      if (sessionsError) throw sessionsError;

      const { data: userRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role, created_at");
      if (rolesError) throw rolesError;

      const { data: subs, error: subsError } = await supabase
        .from("subscriptions")
        .select("user_id, current_period_start, created_at, status");
      if (subsError) throw subsError;

      const currentMonth = new Date().toISOString().slice(0, 7);
      const { data: monthlyUsage, error: usageError } = await supabase
        .from("monthly_usage")
        .select("user_id, ai_responses_count")
        .eq("year_month", currentMonth);
      if (usageError) throw usageError;

      const userStats: UserStats[] = profiles.map((profile: any) => {
        const userForms = allForms?.filter((f) => f.user_id === profile.user_id) || [];
        const userFormIds = userForms.map((f) => f.id);
        const userSessions = allSessions?.filter((s) => userFormIds.includes(s.form_id)) || [];
        const userRole = userRoles?.find((r) => r.user_id === profile.user_id);
        const usage = monthlyUsage?.find((u) => u.user_id === profile.user_id);
        const sub = subs?.find((s) => s.user_id === profile.user_id);
        const roleValue = (userRole?.role as "admin" | "pro" | "free") || "free";
        const planActivatedAt =
          sub?.current_period_start ||
          sub?.created_at ||
          (roleValue !== "free" ? userRole?.created_at ?? null : null);

        return {
          user_id: profile.user_id,
          email: profile.email,
          role: roleValue,
          forms_count: userForms.length,
          total_sessions: userSessions.length,
          total_completed: userSessions.filter((s) => s.status === "completed").length,
          total_pdf_downloads: userSessions.filter((s) => s.pdf_url).length,
          monthly_ai_usage: usage?.ai_responses_count || 0,
          created_at: profile.created_at ?? null,
          plan_activated_at: planActivatedAt ?? null,
        };
      });

      setUsers(userStats);

      const formStats: FormStats[] = (allForms || []).map((form) => {
        const formSessions = allSessions?.filter((s) => s.form_id === form.id) || [];
        const ownerProfile = profiles.find((p: any) => p.user_id === form.user_id);
        return {
          id: form.id,
          title: form.title,
          slug: form.slug,
          is_active: form.is_active,
          owner_email: ownerProfile?.email || "Desconhecido",
          sessions_count: formSessions.length,
          completed_count: formSessions.filter((s) => s.status === "completed").length,
          pdf_downloads: formSessions.filter((s) => s.pdf_url).length,
        };
      });

      setForms(formStats);

      setGlobalStats({
        total_users: profiles.length,
        total_forms: allForms?.length || 0,
        total_sessions: allSessions?.length || 0,
        total_completed: allSessions?.filter((s) => s.status === "completed").length || 0,
        total_pdf_downloads: allSessions?.filter((s) => s.pdf_url).length || 0,
      });
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast({ title: "Erro ao carregar dados", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSetRole = async (userId: string, newRole: "admin" | "pro" | "free") => {
    try {
      await supabase.from("user_roles").delete().eq("user_id", userId);
      if (newRole !== "free") {
        await supabase.from("user_roles").insert({ user_id: userId, role: newRole });
      }
      toast({ title: `Usuário atualizado para ${newRole.toUpperCase()}` });
      fetchData();
    } catch (error) {
      console.error("Error updating role:", error);
      toast({ title: "Erro ao atualizar role", variant: "destructive" });
    }
  };

  const handleToggleFormActive = async (formId: string, currentActive: boolean) => {
    try {
      await supabase.from("forms").update({ is_active: !currentActive }).eq("id", formId);
      toast({ title: currentActive ? "Formulário bloqueado" : "Formulário ativado" });
      fetchData();
    } catch (error) {
      console.error("Error toggling form:", error);
      toast({ title: "Erro ao atualizar formulário", variant: "destructive" });
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke("admin-delete-user", {
        body: { user_id: deleteUser.id },
      });
      if (error) throw error;
      toast({ title: "Usuário excluído", description: deleteUser.email });
      setDeleteUser(null);
      fetchData();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast({
        title: "Erro ao excluir usuário",
        description: error?.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading || planLoading || role !== "admin") {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center">
                <Shield className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-xl font-display font-bold">Painel Admin</span>
            </div>
          </div>
          <Badge variant="secondary">
            <Shield className="mr-1 h-3 w-3" />
            Admin
          </Badge>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 pb-[100px]">
        <div className="flex gap-2 mb-8 flex-wrap">
          <Button variant={activeTab === "overview" ? "default" : "outline"} onClick={() => setActiveTab("overview")}>
            <BarChart3 className="mr-2 h-4 w-4" />
            Visão Geral
          </Button>
          <Button variant={activeTab === "users" ? "default" : "outline"} onClick={() => setActiveTab("users")}>
            <Users className="mr-2 h-4 w-4" />
            Usuários
          </Button>
          <Button variant={activeTab === "forms" ? "default" : "outline"} onClick={() => setActiveTab("forms")}>
            <FileText className="mr-2 h-4 w-4" />
            Formulários
          </Button>
          <Button variant="outline" asChild>
            <Link to="/admin/templates">
              <LayoutTemplate className="mr-2 h-4 w-4" />
              Templates
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/admin/costs">
              <Calculator className="mr-2 h-4 w-4" />
              Custos
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/admin/financial">
              <DollarSign className="mr-2 h-4 w-4" />
              Financeiro
            </Link>
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {activeTab === "overview" && (
              <OverviewTab globalStats={globalStats} users={users} forms={forms} />
            )}
            {activeTab === "users" && (
              <UsersTab
                users={users}
                onSetRole={handleSetRole}
                onRequestDelete={(u) => setDeleteUser(u)}
                onSendGift={handleSendGift}
                sendingGiftTo={sendingGiftTo}
              />
            )}
            {activeTab === "forms" && (
              <FormsTab forms={forms} onToggleActive={handleToggleFormActive} />
            )}
          </>
        )}
      </main>

      <AlertDialog open={!!deleteUser} onOpenChange={(o) => !o && !deleting && setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. A conta <strong>{deleteUser?.email}</strong>, seus formulários,
              leads, respostas e PDFs serão removidos permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                handleDeleteUser();
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? "Excluindo..." : "Excluir definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
